import React from 'react';
import { useResource__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResource';

import { statuses } from '@cube3/state/src/redux/ducks/request-status';

import { UploadQueueListItemUnavailable } from './UploadQueueListItemUnavailable';
import { UploadQueueListItemAssetInProgress } from './UploadQueueListItemAssetInProgress';
import { UploadQueueListItemAssetProcessed } from './UploadQueueListItemAssetProcessed';
import { UploadQueueListItemNoAsset } from './UploadQueueListItemNoAsset';

interface Props {
  uploadId?: string;
  assetId?: string;
  visible?: boolean;
}

const RESOURCE_NOT_FOUND = 'HTTP_NOT_FOUND';

const getErrorCode = (errors) => {
  return errors[0] && errors[0]._error[0] && errors[0]._error[0].code;
};

export const UploadQueueListItem: React.FunctionComponent<Props> = React.memo(
  (props) => {
    const { uploadId, assetId } = props;

    const upload = useResource__ALPHA({
      resourceType: 'file-upload',
      resourceId: uploadId,
      strategy: 'cache-only'
    }).first;

    const assetResource = useResource__ALPHA({
      resourceType: 'asset',
      resourceId: assetId || upload?.asset_id,
      strategy: 'cache-only'
    });

    const asset = assetResource.first;

    const assetDeleted =
      assetResource.status === statuses.FAILED &&
      getErrorCode(assetResource.errors) === RESOURCE_NOT_FOUND;

    const assetUnavailable =
      assetResource.status === statuses.FAILED &&
      getErrorCode(assetResource.errors) !== RESOURCE_NOT_FOUND;

    if (assetDeleted || assetUnavailable) {
      return (
        <UploadQueueListItemUnavailable
          deleted={assetDeleted}
          upload={upload}
          nodeId={assetId || upload?.asset_id}
        />
      );
    }
    if (!asset) {
      return <UploadQueueListItemNoAsset upload={upload} />;
    }
    return <UploadQueueListItemAsset asset={asset} upload={upload} />;
  }
);

const UploadQueueListItemAsset = ({ upload, asset }) => {
  if (
    asset.state === 'processing_complete' ||
    asset.state === 'processing_failed'
  ) {
    return <UploadQueueListItemAssetProcessed upload={upload} asset={asset} />;
  } else {
    return <UploadQueueListItemAssetInProgress upload={upload} asset={asset} />;
  }
};
